import type { FamilyPortalTheme } from "@shared/types/familyPortal";

/**
 * Paint the portal in the premise's own template (PORTAL-5).
 *
 * The server resolves the premise's `SchemeBinding` down to a handful of roles
 * (`familyPortalTheme`); this writes them onto the document as custom
 * properties, and `styles.css` reads roles only. No component ever names a
 * colour, which is what lets a care home recolour its TV scheme and have the
 * portal follow on the next page load.
 *
 * ── Why the fallback lives in CSS, not here ─────────────────────────────────
 *
 * `styles.css` carries the care manifest's values as literals on `:root`. That
 * is deliberate and it is not laziness:
 *
 *   • The sign-in screen has no session, so it has no premise and cannot have a
 *     theme. It still has to look like Foyer.
 *   • The first paint happens before this module has run. Reading the theme out
 *     of JS alone would flash an unstyled page at a relative on a slow phone -
 *     on the screen where they are deciding whether to trust us with a card.
 *   • Importing the manifests here to compute the same defaults would ship 21KB
 *     of nine templates' declarations to use one of them.
 *
 * `theme.test.ts` asserts those literals still match the care manifest, so the
 * duplication is an enforced invariant rather than a comment nobody will
 * honour.
 */

/** Role → custom property. The single place the two vocabularies meet. */
const VARS: Record<keyof Omit<FamilyPortalTheme, "scheme">, string> = {
  paper: "--paper",
  surface: "--surface",
  surfaceAlt: "--surface-alt",
  ink: "--ink",
  inkSoft: "--ink-soft",
  inkMuted: "--ink-muted",
  hairline: "--hairline",
  accent: "--accent",
  accentDeep: "--accent-deep",
  accentTint: "--accent-tint",
  onAccent: "--on-accent",
  sans: "--sans",
  display: "--display",
  mono: "--mono",
};

/**
 * Apply a premise theme, or clear back to the stylesheet defaults when there is
 * none (signed out, or an API old enough not to send one).
 *
 * Clearing rather than re-applying a hard-coded default matters on sign-out: a
 * relative who signs out of Solhem's portal should not be left looking at
 * Solhem's colours while they type another address.
 */
export function applyTheme(theme: FamilyPortalTheme | undefined): void {
  const root = document.documentElement;
  if (!theme) {
    for (const prop of Object.values(VARS)) root.style.removeProperty(prop);
    root.removeAttribute("data-scheme");
    setBrowserChrome(null);
    return;
  }
  for (const [role, prop] of Object.entries(VARS)) {
    const value = theme[role as keyof typeof VARS];
    // A premise whose template omits a role gets the stylesheet's default for
    // it rather than an empty custom property, which would cascade to
    // `initial` and paint black-on-black.
    if (typeof value === "string" && value.trim()) {
      root.style.setProperty(prop, value);
    } else {
      root.style.removeProperty(prop);
    }
  }
  // Diagnostics and nothing else: never branch portal CSS on this. The point of
  // the role vocabulary is that the portal does not know which template it is
  // wearing.
  root.setAttribute("data-scheme", theme.scheme);
  setBrowserChrome(theme.paper);
}

/**
 * Match the phone's browser chrome (the status/URL bar tint on iOS and Android)
 * to the page. Without it a warm-paper portal sits under a slate-grey bar left
 * over from the shipped `<meta name="theme-color">`, which is exactly the kind
 * of seam that makes a page feel like a web view instead of an app.
 */
function setBrowserChrome(color: string | null): void {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (!meta) return;
  if (color) meta.setAttribute("content", color);
  else meta.setAttribute("content", meta.dataset.default ?? "#EFEDE4");
}
